import { useCommonStore, usePersistStore, useSearchStore } from '@/shared/lib/store';
import { TabMenu } from '@/shared/ui';
import { SearchBarType2 } from '@/widgets/search-bar';
import { createFileRoute, useRouter, useSearch, Navigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { publishQueries, usePublishedBoxStore } from '@/entities/sets/publish';
import { NoDataNew } from '@/shared/ui/no-data';
import {
    ByPublishListTab,
    ByClassStudentTab,
    ByNicknameTab,
    DetailPublishBox,
} from '@/widgets/published-box';
import { trackEvent } from 'vs-gs';
import { z } from 'zod';

const searchSchema = z.object({
    tab: z
        .union([
            z.literal('publish'),
            z.literal('class'),
            z.literal('class-detail'),
            z.literal('nickname'),
        ])
        .optional(),
    stdtUserIds: z.string().optional(),
});

export const Route = createFileRoute('/sets/published-box/')({
    validateSearch: (search) => searchSchema.parse(search),
    component: () => <Navigate to="/my-bank" replace />,
});

export function PublishedBoxComponent() {
    const router = useRouter();
    const search: any = useSearch({ strict: false });
    const { tab: searchTab, stdtUserIds } = search as any;
    const [isDetailPublishBox, setIsDetailPublishBox] = useState<boolean>(false);
    const [detailStntId, setDetailStntId] = useState<{
        claName: string;
        userId: string;
        name: string;
    }>();
    const { setInKeyword, setMyBankTab } = useSearchStore();
    const { prevPath } = useCommonStore();
    const {
        tab,
        claIdList,
        setTab,
        resetData,
        setSearchKeyword,
        setFilterClassList,
        setClaIdList,
        setResetTab,
    } = usePublishedBoxStore();
    const { sUserId, sMyClassMemberList } = usePersistStore();
    const [publishWhether, setPublishWhether] = useState<boolean>(false);

    const { data, isFetched } = publishQueries.list.getPubStntClassList({ wrterId: sUserId });

    useEffect(() => {
        if (searchTab) {
            const searchTabValue =
                searchTab === 'publish'
                    ? '발행 리스트별'
                    : searchTab === 'class'
                      ? '클래스 학생별'
                      : searchTab === 'class-detail'
                        ? '클래스 학생별'
                        : searchTab === 'nickname'
                          ? '닉네임별'
                          : undefined;
            searchTabValue && setTab(searchTabValue);
        }
    }, [searchTab]);

    useEffect(() => {
        if (searchTab === 'class-detail') {
            if (stdtUserIds) {
                setIsDetailPublishBox(true);
            }
        }
    }, [stdtUserIds, searchTab]);

    useEffect(() => {
        if (!(prevPath.includes('detail') || prevPath.includes('report'))) {
            setResetTab();
        }
    }, []);

    useEffect(() => {
        if (data && data?.length > 0 && sMyClassMemberList) {
            const filterList = data.filter((item) => item.claId?.split('-')[1] !== sUserId) ?? [];
            const seqList =
                filterList
                    .map((item) => item.claId?.split('-')[4])
                    .filter((item, index, self) => self.indexOf(item) === index) ?? [];
            const claList =
                filterList.filter(
                    (obj, index, self) =>
                        index === self.findIndex((item) => item.claId === obj.claId)
                ) ?? [];
            const filterClass =
                sMyClassMemberList?.filter((item) =>
                    seqList.includes(item.classInfo.classSeq.toString())
                ) ?? [];
            setClaIdList(claList);
            setFilterClassList(filterClass);
            setPublishWhether(true);
        } else {
            setPublishWhether(false);
        }
    }, [data, sMyClassMemberList]);

    /**
     * @param value
     * 탭 클릭 시
     * # 탭 변경
     * # 만약 디테일 상태라면 기본 상태로 전환
     */
    const tabChangeHandler = (value: string) => {
        setInKeyword('');
        setSearchKeyword('');
        resetData();
        setTab(value);
        setIsDetailPublishBox(false);
        setResetTab();
        const tabValue =
            value === '발행 리스트별'
                ? 'publish'
                : value === '클래스 학생별'
                  ? 'class'
                  : value === '닉네임별'
                    ? 'nickname'
                    : undefined;
        router.navigate({ to: '.', search: { tab: tabValue }, replace: true });
    };

    // 클래스 학생별에서 학생이름 클릭 시
    const onByClassStudentClickHandler = (stntData: {
        claName: string;
        userId: string;
        name: string;
    }) => {
        setDetailStntId(stntData);
        setIsDetailPublishBox(true);
        router.navigate({ to: '.', search: { tab: 'class-detail' }, replace: true });
    };

    // 클래스 학생별에서 뒤로가기 클릭 시
    const onByClassStudentBackHandler = () => {
        setIsDetailPublishBox(false);
        router.navigate({ to: '.', search: { tab: 'class' }, replace: true });
    };

    const handleGoCreatedBox = () => {
        const deploy = import.meta.env.VITE_DEPLOY;
        if (deploy === 'prod') {
            trackEvent({
                event: '스마트 문제은행',
                parameter: '내 출제함',
                parameter_value: '내 발행함',
            });
        }
        setMyBankTab('created');
    };

    return (
        <>
            <div className="contwrap floatWrap">
                <a
                    href={import.meta.env.VITE_ENGLISH_MASTER_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="floatBanner ai_english"
                >
                    <span className="blind">AI 영어 지문 마스터로 나만의 문항 만들기</span>
                </a>
                <a
                    href={import.meta.env.VITE_VIVACLASS_DOMAIN}
                    target="_blank"
                    rel="noreferrer"
                    className="floatBanner vivaclass"
                >
                    <span className="blind">비바클래스 수업에서 온라인 평가 활용하기</span>
                </a>
                <div className="contInner">
                    <div className="tabMenu2 subject">
                        <ul>
                            <li onClick={() => setMyBankTab('created')}>
                                <a>내 출제함</a>
                            </li>
                            <li className="on">
                                <a>내 발행함</a>
                            </li>
                        </ul>
                    </div>
                    {publishWhether ? (
                        <>
                            <TabMenu
                                tabItems={[
                                    { tabName: '발행 리스트별', value: '발행 리스트별' },
                                    { tabName: '클래스 학생별', value: '클래스 학생별' },
                                    { tabName: '닉네임별', value: '닉네임별' },
                                ]}
                                selected={tab}
                                className="tab-size1"
                                onChange={tabChangeHandler}
                            />
                            {!isDetailPublishBox ? (
                                <>
                                    <SearchBarType2
                                        claIdList={claIdList}
                                        tab={tab}
                                        title="목록 검색"
                                        placeholder={
                                            (tab === '발행 리스트별'
                                                ? '자료 이름 및 키워드를'
                                                : tab === '클래스 학생별'
                                                  ? '학생 이름을'
                                                  : '닉네임을') + ' 입력하세요.'
                                        }
                                        inputTitle="내 출제함 자료명 검색"
                                    />
                                    {tab === '발행 리스트별' && <ByPublishListTab />}
                                    {tab === '클래스 학생별' && (
                                        <ByClassStudentTab
                                            onDetailProps={onByClassStudentClickHandler}
                                        />
                                    )}
                                    {tab === '닉네임별' && <ByNicknameTab />}
                                </>
                            ) : (
                                // 닉네임별 학생 클릭 시
                                <DetailPublishBox
                                    stntData={detailStntId}
                                    onBackProps={onByClassStudentBackHandler}
                                />
                            )}
                        </>
                    ) : (
                        isFetched && (
                            <NoDataNew
                                titleBlue={'아직 발행한 이력이 없습니다.'}
                                buttonText={'내 출제함'}
                                description="내 출제함에서 과제/평가를 발행해 보세요!"
                                onButtonClick={handleGoCreatedBox}
                            />
                        )
                    )}
                </div>
            </div>
        </>
    );
}
